import type { ReactNode } from 'react';

/**
 * Semantic tones carry MEANING — a red card means something is wrong, a green
 * one means it's fine. Never pick those for looks.
 *
 * The rest (violet/cyan/amber/pink/teal/indigo) are decorative hues that imply
 * no status. They exist so a KPI row of plain counts has rhythm and colour
 * instead of reading as a wall of grey.
 */
export type Tone =
  | 'neutral'
  | 'good'
  | 'warn'
  | 'bad'
  | 'info'
  | 'violet'
  | 'cyan'
  | 'amber'
  | 'pink'
  | 'teal'
  | 'indigo';

export interface Stat {
  key: string;
  label: string;
  value: ReactNode;
  /** One plain-English line explaining where the number comes from. */
  sub?: string;
  icon?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
  active?: boolean;
  /** Optional extra content overlaid on the RIGHT of the card (e.g. small
   *  clickable sub-chips). Rendered as a SIBLING of the card button — not nested
   *  inside it — so its own buttons don't trigger the card's onClick. Cards
   *  without `extra` render exactly as before. */
  extra?: ReactNode;
}

interface ToneStyle {
  card: string;
  value: string;
  /** The icon chip — the strongest colour on the card. */
  chip: string;
  label: string;
  sub: string;
  ring: string;
}

const TONE: Record<Tone, ToneStyle> = {
  neutral: {
    card: 'border-slate-200 bg-gradient-to-br from-white to-slate-100',
    value: 'text-slate-900',
    chip: 'bg-slate-700 text-white',
    label: 'text-slate-600',
    sub: 'text-slate-400',
    ring: 'ring-slate-400',
  },
  good: {
    card: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-100',
    value: 'text-emerald-800',
    chip: 'bg-emerald-600 text-white',
    label: 'text-emerald-700',
    sub: 'text-emerald-600/80',
    ring: 'ring-emerald-400',
  },
  warn: {
    card: 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-100',
    value: 'text-amber-900',
    chip: 'bg-amber-500 text-white',
    label: 'text-amber-800',
    sub: 'text-amber-700/80',
    ring: 'ring-amber-400',
  },
  bad: {
    card: 'border-rose-200 bg-gradient-to-br from-rose-50 to-red-100',
    value: 'text-rose-800',
    chip: 'bg-rose-600 text-white',
    label: 'text-rose-700',
    sub: 'text-rose-600/80',
    ring: 'ring-rose-400',
  },
  info: {
    card: 'border-blue-200 bg-gradient-to-br from-blue-50 to-sky-100',
    value: 'text-blue-800',
    chip: 'bg-blue-600 text-white',
    label: 'text-blue-700',
    sub: 'text-blue-600/80',
    ring: 'ring-blue-400',
  },
  violet: {
    card: 'border-violet-200 bg-gradient-to-br from-violet-50 to-purple-100',
    value: 'text-violet-800',
    chip: 'bg-violet-600 text-white',
    label: 'text-violet-700',
    sub: 'text-violet-600/80',
    ring: 'ring-violet-400',
  },
  cyan: {
    card: 'border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-100',
    value: 'text-cyan-800',
    chip: 'bg-cyan-600 text-white',
    label: 'text-cyan-700',
    sub: 'text-cyan-600/80',
    ring: 'ring-cyan-400',
  },
  amber: {
    card: 'border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-100',
    value: 'text-amber-900',
    chip: 'bg-amber-500 text-white',
    label: 'text-amber-800',
    sub: 'text-amber-700/80',
    ring: 'ring-amber-400',
  },
  pink: {
    card: 'border-pink-200 bg-gradient-to-br from-pink-50 to-rose-100',
    value: 'text-pink-800',
    chip: 'bg-pink-600 text-white',
    label: 'text-pink-700',
    sub: 'text-pink-600/80',
    ring: 'ring-pink-400',
  },
  teal: {
    card: 'border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-100',
    value: 'text-teal-800',
    chip: 'bg-teal-600 text-white',
    label: 'text-teal-700',
    sub: 'text-teal-600/80',
    ring: 'ring-teal-400',
  },
  indigo: {
    card: 'border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-100',
    value: 'text-indigo-800',
    chip: 'bg-indigo-600 text-white',
    label: 'text-indigo-700',
    sub: 'text-indigo-600/80',
    ring: 'ring-indigo-400',
  },
};

/** Decorative hues, in order — gives an untoned KPI row rhythm. */
const PALETTE_CYCLE: Tone[] = ['info', 'violet', 'teal', 'amber', 'cyan', 'pink'];

export default function StatCards({
  stats,
  cols,
  /** Auto-colour cards that didn't ask for a tone, so a row is never a wall of
   *  grey. Cards WITH an explicit tone always keep it — semantics beat decoration.
   *  Pass `colorful={false}` for a row where colour would be noise. */
  colorful = true,
}: {
  stats: Stat[];
  cols?: number;
  colorful?: boolean;
}) {
  const n = cols ?? Math.min(stats.length, 6);
  return (
    <div
      className="stagger grid gap-2 sm:gap-3"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${n > 4 ? 150 : 170}px), 1fr))` }}
    >
      {stats.map((s, i) => {
        const resolved: Tone = s.tone ?? (colorful ? PALETTE_CYCLE[i % PALETTE_CYCLE.length] : 'neutral');
        const tone = TONE[resolved];
        const clickable = !!s.onClick;
        const btnClass = [
          // DS §Cards: 10px radius, elevation-1 at rest, lift on hover.
          'relative overflow-hidden rounded-md border px-3 py-2.5 text-left shadow-card transition-all duration-base ease-out',
          tone.card,
          s.active ? `ring-2 ${tone.ring}` : '',
          clickable ? 'press cursor-pointer hover:-translate-y-0.5 hover:shadow-lift' : 'cursor-default',
        ].join(' ');
        const body = (
          <>
            {/* Corner glow — stops the flat tint from looking like a coloured box. */}
            <span className="pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full bg-white/60 blur-2xl" />
            <div className="relative flex items-center gap-1.5">
              {s.icon && (
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg shadow-sm ${tone.chip}`}>
                  {s.icon}
                </span>
              )}
              <span className={`text-[11px] font-semibold leading-tight ${tone.label}`}>{s.label}</span>
            </div>
            {/* DS §Cards/.kval — Manrope 800, -0.5px tracking, tabular. */}
            <div className={`relative mt-1 font-display text-xl font-extrabold tracking-[-0.5px] tabular-nums sm:text-[22px] ${tone.value}`}>{s.value}</div>
            {s.sub && <div className={`relative mt-0.5 text-[10.5px] leading-tight ${tone.sub}`}>{s.sub}</div>}
          </>
        );
        // With `extra`: card button + an overlay of extra content on the right,
        // as SIBLINGS (never nested) so the extra's own buttons don't fire the
        // card's onClick. Without `extra`: unchanged single-button card.
        if (s.extra) {
          return (
            <div key={s.key} className="relative">
              <button type="button" disabled={!clickable} onClick={s.onClick} className={`${btnClass} h-full w-full`}>
                {body}
              </button>
              <div className="absolute bottom-2 right-2.5 z-10 flex flex-row flex-wrap items-center justify-end gap-1.5">{s.extra}</div>
            </div>
          );
        }
        return (
          <button key={s.key} type="button" disabled={!clickable} onClick={s.onClick} className={btnClass}>
            {body}
          </button>
        );
      })}
    </div>
  );
}
